import { useEffect } from 'react';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { colors } from '@ecosystem/newron-design-system';
import { RetailerKpiOption, SaleType } from '@models/Products';
import { NoResult } from '@pages/NoResult';
import { ExportPerformanceBySegmentResp } from './Types';
import { WithLineChart } from '@components/design';
import {
  createChartAxisConfig,
  processExploreMarketLineChartData,
  processExploreRetailerLineChartData,
} from '@data-visualization/utilities/line-chart/FormatLineChartData';
import { ChartKeys } from '@const/ChartKeys';
import { useExportImages } from '@hooks/export/UseExportChartImage';
import { SaleTypeMarketKpi, SaleTypeRetailerKpi } from '@components/products-line-charts/Types';
import { PageType } from '@utils/pageConfig/Types';
import { generateUniqueChartKeys } from '@data-visualization/utilities/GenerateUniqueChartKeys';
import { getSaleTypeRetailerShareKpi } from '@utils/lineChart/lineChartUtils';

interface Props {
  data: ExportPerformanceBySegmentResp;
  selectedPeriodicity: Periodicity;
  currencySymbol: string;
  selectedLineChartSaleType: SaleType;
  isMarket: boolean;
  noResultHandler: () => void;
  selectedRetailerOption?: RetailerKpiOption;
  noPeriodExistForMulticountry?: boolean;
}

const PerformanceAnalysisLineChart: React.FC<Props> = ({
  data,
  selectedPeriodicity,
  currencySymbol,
  selectedLineChartSaleType = SaleType.revenue,
  isMarket,
  noResultHandler,
  selectedRetailerOption,
  noPeriodExistForMulticountry,
}) => {
  const isEmptyData = noPeriodExistForMulticountry || !data?.salesRecordsFound; //NOSONAR typescript:S6606
  const { getBrandColorMap } = colors.useChartColors();
  const { handleExportImage } = useExportImages(PageType.EXPLORE);

  const isSaleType = (v: string): v is SaleType => {
    return Object.values(SaleType).includes(v.toLowerCase() as SaleType);
  };

  const selectedSaleType = isSaleType(selectedLineChartSaleType)
    ? selectedLineChartSaleType.toLowerCase()
    : SaleType.revenue;

  useEffect(() => {
    if (isEmptyData) noResultHandler();
  }, [isEmptyData]);

  if (isEmptyData) return <NoResult />;

  const mainKpi = isMarket
    ? SaleTypeMarketKpi[selectedSaleType]
    : getSaleTypeRetailerShareKpi(selectedSaleType as SaleType, selectedRetailerOption as RetailerKpiOption);

  const secondKpi = SaleTypeRetailerKpi[selectedSaleType];

  const chartKey = isMarket ? ChartKeys[PageType.EXPLORE].MARKET : ChartKeys[PageType.EXPLORE].RETAILER;

  const chartData = isMarket
    ? processExploreMarketLineChartData(data.kpiDataDto, selectedSaleType).filter(Boolean)
    : processExploreRetailerLineChartData(data.kpiDataDto, selectedSaleType, selectedRetailerOption).filter(Boolean);

  const exportChartKey = isMarket
    ? generateUniqueChartKeys(chartData, ChartKeys[PageType.EXPLORE].MARKET)
    : generateUniqueChartKeys(chartData, ChartKeys[PageType.EXPLORE].RETAILER);

  const chartAxisConfig = isMarket
    ? createChartAxisConfig[PageType.EXPLORE][chartKey](selectedPeriodicity, currencySymbol, mainKpi)
    : createChartAxisConfig[PageType.EXPLORE][chartKey](selectedPeriodicity, currencySymbol, mainKpi, secondKpi);

  const brandColors = chartData.map((chartInfo) => String(chartInfo.groupName).toUpperCase());
  const brandsColorCode = Object.values(getBrandColorMap(brandColors));

  return (
    <div data-testid="performance-analysis-line-chart">
      <WithLineChart
        data={chartData}
        fullWidth
        onExport={(image) => handleExportImage(image, exportChartKey)}
        customLineColors={brandsColorCode}
        {...chartAxisConfig}
      />
    </div>
  );
};

export default PerformanceAnalysisLineChart;
